import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { CirclePause, CirclePlay, Network, Plus } from 'lucide-react';
import type {
  EgressConfig,
  EgressProfileRecord,
  KeyChannelBinding,
  UpstreamChannelRecord,
  UpstreamKeyBucketRecord,
} from '@nadoutong/contracts';
import { parseEgressConfig } from '@nadoutong/contracts';
import {
  useCreateUpstreamChannel,
  useUpdateUpstreamChannel,
  useUpstream,
  useUpstreamChannels,
} from '../../api/useUpstreams.ts';
import { useEgressProfiles } from '../../api/useEgressProfiles.ts';
import { useKeyBucketsList } from '../../api/useKeyBuckets.ts';
import { useKeyChannelBindingsIndex } from '../../api/useKeyChannelBindings.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Input, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { egressLaneLabel } from '../EgressConfigEditor.tsx';
import { InlineTextField } from '../InlineTextField.tsx';
import { Modal } from '../Modal.tsx';
import { coerceErrorMessage, useToast } from '../Toast.tsx';

/** One channel's mounting, as one key bucket's binding states it. */
interface ChannelBindingView {
  readonly bucketId: string;
  readonly bucketLabel: string;
  readonly profileName: string | null;
  /** The EFFECTIVE config: a full-document override wins over the profile. */
  readonly config: EgressConfig | null;
  readonly overridden: boolean;
}

/**
 * The effective typed config of one binding, resolved the way the runtime
 * resolves it for the common cases: a full-document override is consumed
 * directly, no override means the profile document. A PARTIAL fragment
 * override — which the runtime deep-merges — is rendered as the profile
 * document flagged `overridden`, because reproducing the merge here would be
 * a second implementation of `resolveEgressConfig` that drifts silently.
 */
function effectiveBindingConfig(
  binding: KeyChannelBinding,
  profiles: readonly EgressProfileRecord[],
): { readonly config: EgressConfig | null; readonly overridden: boolean } {
  const profile = profiles.find((entry) => entry.id === binding.egress_profile_id);
  const overrideText = binding.egress_config_override_json;
  if (overrideText !== null && overrideText.trim().length > 0 && overrideText.trim() !== '{}') {
    try {
      return { config: parseEgressConfig(JSON.parse(overrideText)), overridden: true };
    } catch {
      return { config: profile?.egress_config ?? null, overridden: true };
    }
  }
  return { config: profile?.egress_config ?? null, overridden: false };
}

/**
 * The axes one mounting states, rendered as `label:value` chips: lane, mode,
 * admission where declared, and the per-lane adapter seats — for the ark lane
 * the vendor and the delivery-quantity coefficient (rendered even at its 1
 * default, so pass-through is a stated fact rather than an absence).
 */
function bindingAxisChips(config: EgressConfig): readonly string[] {
  const chips = [`${egressLaneLabel(config.lane)}`, config.mode];
  if (config.admission !== undefined) chips.push(`admission:${config.admission.mode}`);
  if (config.adapter?.codex !== undefined) {
    chips.push(`codex:${config.adapter.codex.backend}/${config.adapter.codex.transport}`);
  }
  if (config.adapter?.openai?.images !== undefined) {
    chips.push(`images:${config.adapter.openai.images}`);
  }
  if (config.adapter?.openai?.vendor !== undefined) {
    chips.push(`vendor:${config.adapter.openai.vendor}`);
  }
  if (config.adapter?.arkVideo !== undefined) {
    chips.push(`vendor:${config.adapter.arkVideo.vendor}`);
    chips.push(`coefficient:${String(config.adapter.arkVideo.deliveryQuantityCoefficient ?? 1)}`);
  }
  return chips;
}

/**
 * Upstream channel (namespace) catalog — identity and lifecycle only.
 *
 * Lives in the channels drawer off the supply-access section (epic #1327
 * PR-6). It stays a namespace-grain surface: create, rename, enable/disable.
 * What each namespace *exposes* is section 2's model SKU table, and what it
 * costs is that table's inline cost cell — neither belongs here.
 */
export function UpstreamChannelsCard({ upstreamId }: { readonly upstreamId: string }): JSX.Element {
  const t = useT();
  const channels = useUpstreamChannels(upstreamId);
  // The base a channel with no override inherits. Read from the upstream the
  // drawer is already showing rather than a new endpoint, so the inherited
  // value the cell states is the same one the detail header states.
  const upstream = useUpstream(upstreamId);
  const createChannel = useCreateUpstreamChannel(upstreamId);
  const updateChannel = useUpdateUpstreamChannel(upstreamId);
  const keyBucketsQ = useKeyBucketsList(upstreamId);
  const egressProfilesQ = useEgressProfiles();
  const bucketIds = useMemo(
    () => (keyBucketsQ.data ?? []).map((bucket: UpstreamKeyBucketRecord) => bucket.id),
    [keyBucketsQ.data],
  );
  const bindings = useKeyChannelBindingsIndex(upstreamId, bucketIds);
  // Channel id -> the bindings that mount it, across every key bucket.
  // `undefined` while any half is still loading, so "loading" never renders
  // as the definite "not mounted" statement below.
  const bindingsByChannelId = useMemo(() => {
    if (keyBucketsQ.data === undefined || bindings.byBucketId === undefined) return undefined;
    const profiles = egressProfilesQ.data ?? [];
    const byChannel = new Map<string, ChannelBindingView[]>();
    for (const bucket of keyBucketsQ.data) {
      for (const binding of bindings.byBucketId.get(bucket.id) ?? []) {
        const effective = effectiveBindingConfig(binding, profiles);
        const list = byChannel.get(binding.channel_id) ?? [];
        list.push({
          bucketId: bucket.id,
          bucketLabel: bucket.label,
          profileName: binding.egress_profile_name,
          config: effective.config,
          overridden: effective.overridden,
        });
        byChannel.set(binding.channel_id, list);
      }
    }
    return byChannel;
  }, [keyBucketsQ.data, bindings.byBucketId, egressProfilesQ.data]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);

  function handleToggle(channel: UpstreamChannelRecord): void {
    updateChannel.mutate(
      { channelId: channel.channel_id, body: { enabled: !channel.enabled } },
      {
        onSuccess: () => toast.success(t('upstream_detail.channels.toast.updated')),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  function handleRename(channel: UpstreamChannelRecord, channelSlug: string): void {
    updateChannel.mutate(
      { channelId: channel.channel_id, body: { channel_slug: channelSlug } },
      {
        onSuccess: () => toast.success(t('upstream_detail.channels.toast.updated')),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  /** `null` clears the override; the gateway refuses a cross-origin value. */
  function handleEndpointOverride(
    channel: UpstreamChannelRecord,
    endpointUrlOverride: string | null,
  ): void {
    updateChannel.mutate(
      { channelId: channel.channel_id, body: { endpoint_url_override: endpointUrlOverride } },
      {
        onSuccess: () => toast.success(t('upstream_detail.channels.toast.updated')),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  return (
    <article className="flex flex-col gap-5" data-testid="upstream-channels-card">
      <header className="flex items-start justify-between gap-4">
        <p className="m-0 max-w-3xl text-dense text-paper-low">
          {t('upstream_detail.channels.subtitle')}
        </p>
        <Button
          variant="primary"
          onClick={() => setCreateOpen(true)}
          data-testid="upstream-channel-create-button"
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          <span>{t('upstream_detail.channels.create')}</span>
        </Button>
      </header>

      {channels.isLoading ? (
        <div className="h-32 animate-pulse rounded-lg bg-well" />
      ) : channels.isError ? (
        <p className="m-0 text-body text-bad">{coerceErrorMessage(channels.error)}</p>
      ) : (channels.data ?? []).length === 0 ? (
        <div className="rounded-lg bg-well px-4 py-5 text-body text-paper-low">
          {t('upstream_detail.channels.empty')}
        </div>
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>{t('upstream_detail.channels.col.channel')}</Th>
              <Th>{t('upstream_detail.channels.col.label')}</Th>
              <Th>{t('upstream_detail.channels.col.egress_binding')}</Th>
              <Th>{t('upstream_detail.channels.col.endpoint')}</Th>
              <Th>{t('upstream_detail.channels.col.status')}</Th>
              <Th className="text-right">{t('upstream_detail.channels.col.actions')}</Th>
            </tr>
          </THead>
          <TBody>
            {(channels.data ?? []).map((channel) => (
              <Tr key={channel.channel_id}>
                <Td>
                  <div className="flex flex-col">
                    <span className="font-medium text-paper">{channel.channel_slug}</span>
                    <span className="font-mono text-label text-paper-low">{channel.channel_id}</span>
                  </div>
                </Td>
                <Td>
                  <InlineTextField
                    label={t('upstream_detail.channels.label')}
                    initial={channel.channel_slug}
                    testId={`upstream-channel-slug-${channel.channel_id}`}
                    onSubmit={(value) => handleRename(channel, value)}
                  />
                </Td>
                <Td>
                  <ChannelBindingCell
                    views={bindingsByChannelId?.get(channel.channel_id)}
                    loaded={bindingsByChannelId !== undefined}
                  />
                </Td>
                <Td>
                  <ChannelEndpointCell
                    channel={channel}
                    inheritedEndpointUrl={upstream.data?.endpoint_url}
                    pending={updateChannel.isPending}
                    onSubmit={(value) => handleEndpointOverride(channel, value)}
                  />
                </Td>
                <Td>
                  <Chip tone={channel.enabled ? 'ok' : 'neutral'}>
                    {channel.enabled
                      ? t('upstream_detail.channels.enabled')
                      : t('upstream_detail.channels.disabled')}
                  </Chip>
                </Td>
                <Td>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleToggle(channel)}
                      disabled={updateChannel.isPending}
                    >
                      {channel.enabled ? (
                        <CirclePause aria-hidden="true" className="h-3.5 w-3.5" />
                      ) : (
                        <CirclePlay aria-hidden="true" className="h-3.5 w-3.5" />
                      )}
                      <span>
                        {channel.enabled
                          ? t('upstream_detail.channels.disable')
                          : t('upstream_detail.channels.enable')}
                      </span>
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {createOpen ? (
        <CreateUpstreamChannelModal
          pending={createChannel.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(body) => {
            createChannel.mutate(body, {
              onSuccess: () => {
                toast.success(t('upstream_detail.channels.toast.created'));
                setCreateOpen(false);
              },
              onError: (err) => toast.error(coerceErrorMessage(err)),
            });
          }}
        />
      ) : null}
    </article>
  );
}

/**
 * The channel's mounting, inline (#1883 second half).
 *
 * An unmounted channel says so in words — "not mounted" — rather than with an
 * empty cell, because an empty cell reads as "nothing to see" while the fact
 * it states is "task SKUs on this channel are not routable and every sync
 * request runs on the synthesized default profile".
 */
function ChannelBindingCell({
  views,
  loaded,
}: {
  readonly views: readonly ChannelBindingView[] | undefined;
  readonly loaded: boolean;
}): JSX.Element {
  const t = useT();
  if (!loaded) {
    return <span className="text-label text-paper-low">…</span>;
  }
  if (views === undefined || views.length === 0) {
    return (
      <Chip tone="neutral" testId="channel-binding-unmounted">
        {t('upstream_detail.channels.binding.unmounted')}
      </Chip>
    );
  }
  return (
    <div className="flex flex-col gap-1.5" data-testid="channel-binding-cell">
      {views.map((view) => (
        <div key={view.bucketId} className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-micro text-paper-low">{view.bucketLabel}</span>
          {view.config === null ? (
            <span className="text-label text-paper-low">
              {view.profileName ?? t('upstream_detail.channels.binding.unresolved')}
            </span>
          ) : (
            bindingAxisChips(view.config).map((chip) => (
              <span
                key={chip}
                className="inline-flex rounded-full bg-well px-2 py-0.5 font-mono text-micro text-paper-mid"
              >
                {chip}
              </span>
            ))
          )}
          {view.overridden ? (
            <span className="inline-flex rounded-full bg-warn-dim px-2 py-0.5 font-mono text-micro text-warn">
              {t('upstream_detail.channels.binding.overridden')}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The base this channel dials, and the affordance to re-point it (#2465).
 *
 * Inheritance is stated as the INHERITED VALUE in grey, not as an empty cell:
 * "this lane dials the upstream's base" is the fact, and an operator deciding
 * whether a supplier's second protocol leg is reachable needs to see which
 * base that is without opening another surface.
 *
 * Clearing is a separate button rather than submitting an empty field, because
 * `InlineTextField` reverts an empty submission by design — every other
 * consumer edits a required identifier there, where "" is a slip. Here it is a
 * real intent, so it gets a control of its own instead of a shared-component
 * change that would make "" meaningful for those callers too.
 *
 * "Override" OPENS AN EDITOR; it does not write. The earlier version submitted
 * the inherited value on click, which reads as "let me look at this" and
 * stores "pin this channel to today's base" — two failures, both silent:
 * a later same-origin edit of the upstream base (which the gateway allows)
 * would leave this channel behind on the old path while the cell showed its
 * own override as if intended; and an unrelated later attempt to move the
 * upstream to another host would be refused by the reverse gate naming a
 * channel that never needed an override at all. A row is only written once
 * the operator submits a value they actually typed.
 */
function ChannelEndpointCell({
  channel,
  inheritedEndpointUrl,
  pending,
  onSubmit,
}: {
  readonly channel: UpstreamChannelRecord;
  readonly inheritedEndpointUrl: string | undefined;
  readonly pending: boolean;
  readonly onSubmit: (value: string | null) => void;
}): JSX.Element {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const override = channel.endpoint_url_override;
  if (override === null && !editing) {
    return (
      <div className="flex flex-col items-start gap-1">
        <span
          className="font-mono text-micro text-paper-low"
          data-testid={`upstream-channel-endpoint-inherited-${channel.channel_id}`}
        >
          {inheritedEndpointUrl ?? '…'}
        </span>
        <span className="text-micro text-paper-low">
          {t('upstream_detail.channels.endpoint.inherited')}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={pending}
          onClick={() => setEditing(true)}
          data-testid={`upstream-channel-endpoint-adopt-${channel.channel_id}`}
        >
          {t('upstream_detail.channels.endpoint.override')}
        </Button>
      </div>
    );
  }
  if (override === null) {
    // Editing an inherited channel: the field is SEEDED with the inherited
    // base so the operator edits a path rather than retyping a host, but
    // nothing is stored until they submit. Cancel returns to inheriting
    // without a write.
    return (
      <div className="flex flex-col items-start gap-1">
        <InlineTextField
          label={t('upstream_detail.channels.endpoint.label')}
          initial={inheritedEndpointUrl ?? ''}
          mono
          testId={`upstream-channel-endpoint-${channel.channel_id}`}
          onSubmit={(value) => {
            setEditing(false);
            onSubmit(value);
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          disabled={pending}
          onClick={() => setEditing(false)}
          data-testid={`upstream-channel-endpoint-cancel-${channel.channel_id}`}
        >
          {t('common.cancel')}
        </Button>
        <span className="text-micro text-paper-low">
          {t('upstream_detail.channels.endpoint.hint')}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <InlineTextField
        label={t('upstream_detail.channels.endpoint.label')}
        initial={override}
        mono
        testId={`upstream-channel-endpoint-${channel.channel_id}`}
        onSubmit={(value) => onSubmit(value)}
      />
      <Button
        size="sm"
        variant="ghost"
        disabled={pending}
        onClick={() => onSubmit(null)}
        data-testid={`upstream-channel-endpoint-reset-${channel.channel_id}`}
      >
        {t('upstream_detail.channels.endpoint.reset')}
      </Button>
      <span className="text-micro text-paper-low">
        {t('upstream_detail.channels.endpoint.hint')}
      </span>
    </div>
  );
}

function CreateUpstreamChannelModal({
  pending,
  onClose,
  onSubmit,
}: {
  readonly pending: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (body: {
    readonly channel_id: string;
    readonly channel_slug: string;
    readonly endpoint_url_override?: string | null;
  }) => void;
}): JSX.Element {
  const t = useT();
  const [channelId, setChannelId] = useState('');
  const [channelSlug, setChannelSlug] = useState('');
  const [endpointOverride, setEndpointOverride] = useState('');
  const disabled = channelId.trim().length === 0 || channelSlug.trim().length === 0 || pending;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (disabled) {
      return;
    }
    const trimmedOverride = endpointOverride.trim();
    onSubmit({
      channel_id: channelId.trim(),
      channel_slug: channelSlug.trim(),
      // Omitted rather than sent as null when blank: a create with no opinion
      // about the base should not have to state one.
      ...(trimmedOverride.length > 0 ? { endpoint_url_override: trimmedOverride } : {}),
    });
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!pending) onClose();
      }}
      titleId="create-upstream-channel-title"
      testId="create-upstream-channel-modal"
    >
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit}>
        <div className="flex items-center gap-2">
          <Network aria-hidden="true" className="h-5 w-5 text-signal" />
          <h2 id="create-upstream-channel-title" className="m-0 text-title text-paper">
            {t('upstream_detail.channels.modal.title')}
          </h2>
        </div>
        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.channels.modal.channel_id')}
          </span>
          <Input
            value={channelId}
            onChange={(e) => setChannelId(e.currentTarget.value)}
            disabled={pending}
            autoComplete="off"
            spellCheck={false}
            mono
            aria-label={t('upstream_detail.channels.modal.channel_id')}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.channels.modal.channel_slug')}
          </span>
          <Input
            value={channelSlug}
            onChange={(e) => setChannelSlug(e.currentTarget.value)}
            disabled={pending}
            autoComplete="off"
            spellCheck={false}
            aria-label={t('upstream_detail.channels.modal.channel_slug')}
          />
          <span className="text-label text-paper-low">
            {t('upstream_detail.channels.modal.hint')}
          </span>
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">
            {t('upstream_detail.channels.modal.endpoint')}
          </span>
          <Input
            value={endpointOverride}
            onChange={(e) => setEndpointOverride(e.currentTarget.value)}
            disabled={pending}
            autoComplete="off"
            spellCheck={false}
            mono
            aria-label={t('upstream_detail.channels.modal.endpoint')}
          />
          <span className="text-label text-paper-low">
            {t('upstream_detail.channels.endpoint.hint')}
          </span>
        </label>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={disabled} loading={pending}>
            {pending ? t('common.saving') : t('upstream_detail.channels.modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
